import type { ProductFeedItem } from '__generated__/api/unauth/types.gen';
import { Divider } from '@common/components';
import { SvgStarFill } from '@common/icon';
import { cn } from '@core/utils';
import Link from 'next/link';

import { Image } from '@/components/client';
import { WEL_MARKET_ROUTES } from '@/constants/routes';
import { formatPrice } from '@/utils/formatPrice';

interface ProductCardProps {
  product: ProductFeedItem;
}

export const ProductCard = ({ product }: ProductCardProps) => {
  const {
    productId,
    productName,
    mainImageUrl,
    price,
    review,
    tags,
    salePhase,
    soldOut,
  } = product;
  const isSoldOut = soldOut && salePhase === 'ON_SALE';
  const isNotOnSale =
    (salePhase !== 'ON_SALE' && salePhase !== undefined) || isSoldOut;
  const salePhaseLabel = isSoldOut
    ? '품절'
    : salePhase === 'SALE_ENDED'
      ? '판매 종료'
      : '상품 준비중이에요';

  const regularPrice = price?.regularPrice ?? 0;
  const expectedFinalPrice = price?.expectedFinalPrice ?? regularPrice;
  const discountRate = price?.totalDiscountRate ?? 0;
  const hasDiscount = expectedFinalPrice !== regularPrice;
  const priceColor = hasDiscount ? 'text-Gray-550' : 'text-Gray-black';

  return (
    <Link
      href={WEL_MARKET_ROUTES.PRODUCT_DETAIL.pathname({
        productNumber: productId ?? '',
      })}
      className="flex flex-col gap-20 mobile-tablet:gap-12"
    >
      {/* 썸네일 */}
      <div className="relative aspect-square w-full overflow-hidden">
        {mainImageUrl ? (
          <Image
            src={mainImageUrl}
            alt={productName ?? ''}
            fill
            className="rounded-none object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-Gray-200" />
        )}
        {isNotOnSale && (
          <div className="absolute inset-0 flex items-center justify-center bg-Gray-black/40">
            <p className="font-700 text-Gray-white typo-body2 mobile-tablet:typo-body3">
              {salePhaseLabel}
            </p>
          </div>
        )}
      </div>

      {/* 상품 정보 */}
      <div className="flex flex-col gap-24 px-8 mobile-tablet:gap-16">
        {/* 상품명 + 배지 */}
        <div className="flex flex-col gap-8 mobile-tablet:gap-4">
          <p className="line-clamp-2 font-500 text-Gray-900 typo-body1 mobile-tablet:typo-body2">
            {productName}
          </p>
          {tags && tags.length > 0 && (
            <div className="flex gap-4">
              {tags.map((tag, index) => (
                <span
                  key={`${tag.tagCode}-${index}`}
                  className="rounded-xsmall px-6 py-2 font-600 typo-caption1"
                  style={{
                    backgroundColor: tag.backgroundColor ?? undefined,
                    color: tag.textColor ?? undefined,
                  }}
                >
                  {tag.tagName}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* 가격 */}
        <div className="flex items-center gap-20">
          {/* 일반가 */}
          <div className="flex flex-col gap-2">
            <span className={cn('font-600 typo-body4', priceColor)}>
              일반가
            </span>
            <span
              className={cn(
                'font-700 typo-body1 mobile-tablet:typo-body2',
                priceColor,
              )}
            >
              {formatPrice(regularPrice)}
            </span>
          </div>

          {/* 구분선 + 최대할인가 (할인 있을 때만) */}
          {hasDiscount && (
            <>
              <Divider variant="vertical" className="h-36 !bg-Gray-300" />

              <div className="flex flex-1 flex-col">
                <span className="font-600 text-Gray-black typo-body4">
                  최대할인가
                </span>
                <div className="flex items-center gap-4">
                  <span className="font-700 text-Gray-black typo-body1 mobile-tablet:typo-body2">
                    {formatPrice(expectedFinalPrice)}
                  </span>
                  {discountRate > 0 && (
                    <span className="font-700 text-R-400 typo-body1 mobile-tablet:typo-body2">
                      {discountRate}%
                    </span>
                  )}
                </div>
              </div>
            </>
          )}

          {/* 별점 */}
          <div className="ml-auto flex flex-col items-center gap-4">
            <SvgStarFill className="size-20 text-Y-550 mobile-tablet:size-14" />
            <div className="flex items-center gap-2">
              <span className="font-700 text-Gray-600 typo-body3 mobile-tablet:typo-body4">
                {review?.averageRating ?? 0}
              </span>
              <span className="font-400 text-Gray-600 typo-body3 mobile-tablet:typo-body4">
                ({review?.reviewCount ?? 0})
              </span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};
